import { useState } from 'react'
import { Switch, Tooltip, Popconfirm, message, theme } from 'antd'
import { Horizontal } from '@gravitate-js/excalibrr'
import type { GridApi } from 'ag-grid-community'
interface InvoiceHoldColumnProps {
  value: boolean
  data: {
    supplier_id: string
    supplier_name: string
    hold_reason?: string
    hold_applied_at?: string
  }
  api: GridApi
  context: {
    onToggleHold: (supplierId: string, hold: boolean) => Promise<void>
  }
}

export function InvoiceHoldColumn({ value: isOnHold, data, api, context }: InvoiceHoldColumnProps) {
  const { token } = theme.useToken()
  const [loading, setLoading] = useState(false)
  const [popconfirmVisible, setPopconfirmVisible] = useState(false)
  const { onToggleHold } = context
  const handleToggle = async (checked: boolean) => {
    setPopconfirmVisible(true)
  }

  const handleConfirm = async () => {
    try {
      setLoading(true)
      const newHoldStatus = !isOnHold
      await onToggleHold(data.supplier_id, newHoldStatus)
      message.success(
        newHoldStatus
          ? `${data.supplier_name} invoices are now on hold`
          : `${data.supplier_name} invoices are now being processed normally`
      )
      setPopconfirmVisible(false)
    } catch (error) {
      message.error('Failed to update invoice processing status')
      console.error('Error updating invoice hold status:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleString()
  }

  const tooltipTitle = isOnHold
    ? `On hold since ${formatDate(data.hold_applied_at)}${data.hold_reason ? `: ${data.hold_reason}` : ''}`
    : 'Invoices are being processed normally'

  return (
    <Horizontal justifyContent='center'>
      <Popconfirm
        title={isOnHold ? 'Resume Invoice Processing' : 'Place Invoices on Hold'}
        description={`Are you sure you want to ${
          isOnHold ? 'remove the hold and resume processing invoices' : 'place a hold on invoice processing'
        } for ${data.supplier_name}?`}
        onConfirm={handleConfirm}
        okText='Yes'
        cancelText='No'
        open={popconfirmVisible}
        onCancel={() => setPopconfirmVisible(false)}
      >
        <Tooltip title={tooltipTitle}>
          <Switch
            checked={!isOnHold}
            onChange={handleToggle}
            loading={loading}
            style={{
              backgroundColor: !isOnHold ? token.colorSuccess : token.colorWarning,
            }}
            checkedChildren='Active'
            unCheckedChildren='On Hold'
          />
        </Tooltip>
      </Popconfirm>
    </Horizontal>
  )
}
